import { toast } from "sonner";
import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Lightbulb, ArrowRight, Plus, Trash2, Save, Clock, Download, BookOpen } from "lucide-react";
import api from "@/api";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import OldSubmit from "./oldsubmit";
import { useLayoutEffect } from "react";

const SubmitIdea = () => {
  useLayoutEffect(() => { window.scrollTo(0, 0); }, []);
  const [submissionType, setSubmissionType] = useState("update"); // "new" or "update"
  const [teamMembers, setTeamMembers] = useState([
    { role: "Team Leader"},
    { role: "Team Mentor" },
    { role: "Team Member - 1" }, 
  ]);
const [missingFields, setMissingFields] = useState([]);
  // Add these lines with your other state declarations
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [allDrafts, setAllDrafts] = useState([]);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formFields, setFormFields] = useState({});
  const [teamMembersLoaded, setTeamMembersLoaded] = useState(false);
  const [showAITechExample, setShowAITechExample] = useState(false);
  const [showBestTeamExample, setShowBestTeamExample] = useState(false);
  const [showSupportExample, setShowSupportExample] = useState(false);

  // Add this state at the top of your component
  const [showToBeExample, setShowToBeExample] = useState(false);
  const [showChangeExample, setShowChangeExample] = useState(false);
  const [showAsIsExample, setShowAsIsExample] = useState(false);
  const [showBenefitsExample, setShowBenefitsExample] = useState(false);
  
  // STATE
  const [references, setReferences] = useState([1]); // show only 1 by default

  // ADD NEW REFERENCE FIELD
  const addReference = () => {
    setReferences([...references, references.length + 1]);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);
    
    // Warn user before leaving if there are unsaved changes
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load draft when user is logged in and submission type is new
  useEffect(() => {
    if (isLoggedIn && submissionType === "new") {
      const timer = setTimeout(() => {
        loadDraft();
        loadAllDrafts();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, submissionType]);

  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [useCaseId, setUseCaseId] = useState("");
  const formRef = useRef({});
  const autoSaveTimeoutRef = useRef(null);

  const query = new URLSearchParams(location.search);
  const emailFromQuery = query.get("email");

  // Add this after the existing useEffects, around line 60
  useEffect(() => {
    const type = query.get("type");
    if (type === "new") {
      setSubmissionType("new");
    }
  }, [location.search]);

  // Debug effects
  useEffect(() => {
    console.log('Team members updated:', teamMembers);
  }, [teamMembers]);

  useEffect(() => {
    console.log('formRef current state:', formRef.current);
  }, [formFields]);

  // ------------------------------------------------------------------
  // AUTO FILL EMAIL WHEN ?email= IS PRESENT → UPDATE MODE
  // ------------------------------------------------------------------
  useEffect(() => {
    if (emailFromQuery) {
      setSubmissionType("update");
      setEmail(emailFromQuery);
      formRef.current.email = emailFromQuery;
    }
  }, [emailFromQuery]);

  // ------------------------------------------------------------------
  // GET FORM DATA HELPER
  // ------------------------------------------------------------------
 const getFormData = () => {
    const teamMembersPayload = teamMembers.map((member) => ({
      role: member.role,
      firstName: formRef.current[`${member.role}_firstName`] || "",
      lastName: formRef.current[`${member.role}_lastName`] || "",
      email: formRef.current[`${member.role}_email`] || "",
      empId: formRef.current[`${member.role}_empId`] || "",
      department: formRef.current[`${member.role}_department`] || "",
      designation: formRef.current[`${member.role}_designation`] || "",
      experience: formRef.current[`${member.role}_experience`] || "",
      location: formRef.current[`${member.role}_location`] || "",
      skill: formRef.current[`${member.role}_skill`] || "",
    }));

    const payload = {
      submissionType,
      ...formRef.current,
      teamMembers: teamMembersPayload,
    };

    // CLEAN extra role fields
    Object.keys(payload).forEach((key) => {
      if (
        /_firstName|_lastName|_email|_empId|_department|_designation|_experience|_location|_skill$/.test(
          key
        )
      ) {
        delete payload[key];
      }
    });

    return payload;
  };

  // ------------------------------------------------------------------
  // LOAD ALL DRAFTS FUNCTION
  // ------------------------------------------------------------------
  const loadAllDrafts = async () => {
    if (!isLoggedIn) return;
    
    setIsLoadingDrafts(true);
    try {
      const token = localStorage.getItem("token");
      const res = await api.get('/drafts', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Combine both drafts and autosaves
      const combinedDrafts = [
        ...(res.data.drafts || [])
      ];
      
      // Sort by updated_at (newest first) and add display labels
      const sortedDrafts = combinedDrafts
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        .map((draft, index) => ({
          ...draft,
          displayLabel: draft.type === 'draft' 
            ? `Draft ${index + 1}` 
            : `Draft ${index + 1}`,
          timestamp: new Date(draft.updated_at).toLocaleString(),
          shortTime: new Date(draft.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }));
      
      setAllDrafts(sortedDrafts);
      setSavedDrafts(sortedDrafts.filter(d => d.type === 'draft')); // Keep for modal
    } catch (error) {
      console.error('Error loading all drafts:', error);
    } finally {
      setIsLoadingDrafts(false);
    }
  };

  // ------------------------------------------------------------------
  // AUTO-SAVE FUNCTIONALITY
  // ------------------------------------------------------------------
  const autoSave = async () => {
    if (!isLoggedIn || submissionType !== "new") return;
    
    const formData = getFormData();
    
    const hasData = Object.keys(formData).some(key => {
      if (key === 'teamMembers') {
        return formData.teamMembers.some(member => 
          Object.values(member).some(value => value && value.toString().trim() !== '')
        );
      }
      return formData[key] && formData[key].toString().trim() !== '';
    });

    if (hasData) {
      setIsAutoSaving(true);
      try {
        const token = localStorage.getItem("token");
        const payload = {
          formData: formData,
          teamMembers: teamMembers
        };

        if (draftId) {
          // Use autosave-specific endpoint
          await api.put(`/drafts/autosave/${draftId}`, payload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
        } else {
          // Create new autosave
          const res = await api.post('/drafts', {
            ...payload,
            type: 'autosave'
          }, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          
          if (res.data.draftId) {
            setDraftId(res.data.draftId);
          }
        }
        
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        setMissingFields([]); // Clear missing fields on successful submit
        
        // Refresh the drafts list after auto-save
        loadAllDrafts();
      } catch (error) {
        console.error('Auto-save failed:', error);
      } finally {
        setIsAutoSaving(false);
      }
    }
  };

  // Start auto-save timer on any change
  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    if (hasUnsavedChanges) {
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 30000); // 30 seconds
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [hasUnsavedChanges]);

  // ------------------------------------------------------------------
  // LOAD DRAFT FUNCTIONALITY
  // ------------------------------------------------------------------
  const loadDraft = async () => {
    try {
      const token = localStorage.getItem("token");
      console.log('Loading draft...');
      
      // Use the new draft endpoint
      const res = await api.get('/drafts/latest', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Draft API response:', res.data);

      if (res.data.draft) {
        const draft = res.data.draft;
        const formData = draft.formData || {};
        const savedTeamMembers = draft.formData.teamMembers || [];
        
        console.log('Draft formData:', formData);
        console.log('Draft teamMembers:', savedTeamMembers);
        
        setDraftId(draft.id);
        
        // Show draft type in UI
        if (draft.type === 'autosave') {
          toast.info('Loaded autosaved draft');
        } else {
          toast.info('Loaded saved draft');
        }
        
        // FIRST set the team members
        if (savedTeamMembers.length > 0) {
          setTeamMembers(savedTeamMembers);
        }
        
        // THEN populate form fields after a small delay to ensure team members are set
        setTimeout(() => {
          const updatedFields = {};

          // Main fields
          Object.keys(formData).forEach(key => {
            if (key !== "teamMembers") {
              formRef.current[key] = formData[key];
              updatedFields[key] = formData[key];
            }
          });

          // Team member fields
          savedTeamMembers.forEach(member => {
            const role = member.role;
            [
              "firstName", "lastName", "email", "empId",
              "department", "designation", "experience", "location", "skill"
            ].forEach(field => {
              const fieldName = `${role}_${field}`;
              const value = member[field] || "";
              
              formRef.current[fieldName] = value;
              updatedFields[fieldName] = value;
            });
          });

          // This finally updates UI correctly
          setFormFields(prev => ({ ...prev, ...updatedFields }));
        }, 100);

        setHasUnsavedChanges(true);
        setLastSaved(new Date(draft.updated_at));
        setTeamMembersLoaded(true);
      } else {
        console.log('No draft found');
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  // ------------------------------------------------------------------
  // SAVE AS DRAFT FUNCTIONALITY (Manual save)
  // ------------------------------------------------------------------
  const saveAsDraft = async () => {
    if (!isLoggedIn) {
      toast.error("Please login to save draft");
      return;
    }

    const formData = getFormData();
    
    const hasData = Object.keys(formData).some(key => {
      if (key === 'teamMembers') {
        return formData.teamMembers.some(member => 
          Object.values(member).some(value => value && value.toString().trim() !== '')
        );
      }
      return formData[key] && formData[key].toString().trim() !== '';
    });
    
    if (!hasData) {
      toast.error("No data to save as draft");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const payload = {
        formData: formData,
        teamMembers: teamMembers,
        type: 'draft'  // Explicitly save as manual draft
      };

      // Always create a new manual draft
      const res = await api.post('/drafts', payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data.draftId) {
        setDraftId(res.data.draftId);
      }

      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      setMissingFields([]); // Clear missing fields on successful submit
      // Refresh the drafts list after saving
      loadAllDrafts();
      
      toast.success('Draft saved successfully!');
    } catch (error) {
      console.error('Save as draft failed:', error);
      toast.error('Failed to save draft');
    }
  };

  // ------------------------------------------------------------------
  // LOAD SAVED DRAFTS LIST
  // ------------------------------------------------------------------
  const loadSavedDrafts = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await api.get('/drafts/saved/list', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Filter out autosaves, only show manual drafts
      const manualDrafts = res.data.drafts.filter(draft => draft.type === 'draft');
      setSavedDrafts(manualDrafts);
    } catch (error) {
      console.error('Error loading saved drafts:', error);
    }
  };

  const loadSpecificDraft = async (draftId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await api.get(`/drafts/${draftId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (res.data.draft) {
        const draft = res.data.draft;
        const formData = draft.formData || {};
        const savedTeamMembers = draft.formData.teamMembers || [];
        
        setDraftId(draft.id);
        setTeamMembers(savedTeamMembers);
        
        setTimeout(() => {
          const updatedFields = {};
          
          Object.keys(formData).forEach(key => {
            if (key !== "teamMembers") {
              formRef.current[key] = formData[key];
              updatedFields[key] = formData[key];
            }
          });

          savedTeamMembers.forEach(member => {
            const role = member.role;
            [
              "firstName", "lastName", "email", "empId",
              "department", "designation", "experience", "location", "skill"
            ].forEach(field => {
              const fieldName = `${role}_${field}`;
              const value = member[field] || "";
              
              formRef.current[fieldName] = value;
              updatedFields[fieldName] = value;
            });
          });

          setFormFields(prev => ({ ...prev, ...updatedFields }));
        }, 100);
        
        setHasUnsavedChanges(true);
        setShowDraftsModal(false);
        toast.success(`Loaded draft: ${draft.id}`);
      }
    } catch (error) {
      console.error('Error loading specific draft:', error);
    }
  };

  // ------------------------------------------------------------------
  // HANDLE FORM CHANGES
  // ------------------------------------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    formRef.current[name] = value;
    setFormFields(prev => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
  };

  const handleNewIdeaClick = () => {
    const token = localStorage.getItem("token");
   
    if (!token) {
      const emailValue = formRef.current.email?.trim() || "";
      navigate(`/login?email=${encodeURIComponent(emailValue)}`);
      return;
    }
   
    // Preserve email in URL when logged in
    if (emailFromQuery) {
      navigate(`/submit-idea?email=${encodeURIComponent(emailFromQuery)}`, {
        replace: true,
      });
    }
   
    setSubmissionType("new");
  };

  const addMember = () => {
    // Find all existing "Team Member - X" roles and get their numbers
    const memberNumbers = teamMembers
      .map(m => {
        const match = m.role.match(/^Team Member - (\d+)$/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(n => n > 0);
    
    // Get the next number (max + 1, or 1 if none exist)
    const nextNumber = memberNumbers.length > 0 ? Math.max(...memberNumbers) + 1 : 1;
    
    const newMember = { role: `Team Member - ${nextNumber}` };
    setTeamMembers([...teamMembers, newMember]);
    setHasUnsavedChanges(true);
  };

  const removeMember = (index) => {
    setTeamMembers(teamMembers.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  };

  const handleOldLogin = () => {
    const emailValue = formRef.current.email?.trim();

    if (!emailValue) {
      toast.error("Please enter your email", { duration: 3000 });
      return;
    }

    navigate(`/login?email=${encodeURIComponent(emailValue)}`);
  };

  const handleSubmit = async () => {
    const requiredFields =
      submissionType === "new"
        ? [
            "teamName",
            "workCenter",
            "pocDone",
            "useCaseTitle",
            "businessProblem",
          ]
        : ["previousIdeaId", "updateSummary"];

    const missing = requiredFields.filter((field) => {
      const value = formRef.current[field];

      // Handle undefined / null
      if (value === undefined || value === null) return true;

      // Handle string fields
      if (typeof value === "string" && value.trim() === "") return true;

      // Handle arrays
      if (Array.isArray(value) && value.length === 0) return true;

      // Handle objects
      if (typeof value === "object" && Object.keys(value).length === 0)
        return true;

      // Boolean (true/false) is always valid
      // Number is valid unless NaN
      return false;
    });

if (missing.length > 0) {
  // Convert camelCase to readable text
  const toTitle = (str) =>
    str.replace(/([A-Z])/g, " $1")
       .replace(/^./, (c) => c.toUpperCase());

  // Set the missing fields in state to highlight them
  setMissingFields(missing);
  
  toast.error(
    "Required fields missing: " + missing.map((m) => toTitle(m)).join(", "),
    { duration: 3000 }
  );
  return;
}

    const payload = getFormData();

    try {
      const token = localStorage.getItem("token");

      // Delete draft if submitting from draft
      if (draftId) {
        try {
          await api.delete(`/ideas/drafts/${draftId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (error) {
          console.error("Error deleting draft:", error);
        }
      }

      const res = await api.post("/ideas/submit", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const useCaseId = res.data?.useCaseId || res.data?.id || "N/A";
      toast.success(
        `Idea submitted successfully! Generated Use Case ID: ${useCaseId}`
      );

      setUseCaseId(useCaseId);
      setShowModal(true);
      setDraftId(null);
      setHasUnsavedChanges(false);
      setMissingFields([]); // Clear missing fields on successful submit
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      toast.error("Submission Failed: " + msg, { duration: 3000 });
    }
  };

  // ------------------------------------------------------------------
  // REUSABLE COMPONENTS
  // ------------------------------------------------------------------
  const Section = ({ title, children }) => (
    <div className="mb-10">
      <h2 className="text-2xl font-bold mb-4 text-white ">
        {title}
      </h2>

      <Card className="bg-black/40  transition-all duration-300">
        <CardContent className="space-y-6 p-6">{children}</CardContent>
      </Card>
    </div>
  );

const InputField = ({ label, name, placeholder, required, teamMemberField = false }) => {
  const handleChange = (e) => {
    const newValue = e.target.value;
    formRef.current[name] = newValue;
    setHasUnsavedChanges(true);
  };
  
  // Check if this field is in the missing fields list
  const isMissing = missingFields.includes(name);
  
  return (
    <div>
      {label && (
        <Label className="text-white text-sm mb-1 block">
          {label} {required && <span className="text-green-400">*</span>}
        </Label>
      )}

      <Input
        name={name}
        placeholder={placeholder}
        defaultValue={formRef.current[name] ?? ""}
        onChange={handleChange}
        disabled={name === "email" && Boolean(emailFromQuery)}
        className={`bg-black/40 text-white placeholder-gray-400 focus:ring-green-500 ${
          isMissing 
            ? 'border-2 border-red-500 focus:border-red-500' 
            : 'border border-green-600/40 focus:border-green-500'
        } ${
          name === "email" && emailFromQuery ? "opacity-60 cursor-not-allowed" : ""
        }`}
      />
    </div>
  );
};
const TextareaField = ({ label, name, placeholder, required }) => {
  const handleChange = (e) => {
    const newValue = e.target.value;
    formRef.current[name] = newValue;
    setHasUnsavedChanges(true);
  };

  // Check if this field is in the missing fields list
  const isMissing = missingFields.includes(name);

  return (
    <div>
      {label && (
        <Label className="text-white text-sm mb-1 block">
          {label} {required && <span className="text-green-400">*</span>}
        </Label>
      )}

      <Textarea
        name={name}
        placeholder={placeholder}
        defaultValue={formRef.current[name] ?? ""}
        onChange={handleChange}
        className={`bg-black/40 text-white placeholder-gray-400 focus:ring-green-500 ${
          isMissing 
            ? 'border-2 border-red-500 focus:border-red-500' 
            : 'border border-green-600/40 focus:border-green-500'
        }`}
      />
    </div>
  );
};
  // ------------------------------------------------------------------
  // MAIN RETURN UI
  // ------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#002000] to-black text-gray-100 py-16 px-6 relative overflow-hidden">
      {/* Auto-save indicator */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {hasUnsavedChanges && !isAutoSaving && (
          <div className="flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/50 text-yellow-300 px-3 py-2 rounded-lg">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Unsaved changes</span>
          </div>
        )}
        {isAutoSaving && (
          <div className="flex items-center gap-2 bg-blue-500/20 border border-blue-500/50 text-blue-300 px-3 py-2 rounded-lg">
            <Clock className="w-4 h-4 animate-spin" />
            <span className="text-sm">Auto-saving...</span>
          </div>
        )}
        {lastSaved && !isAutoSaving && !hasUnsavedChanges && (
          <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/50 text-green-300 px-3 py-2 rounded-lg">
            <Save className="w-4 h-4" />
            <span className="text-sm">
              Saved {lastSaved.toLocaleTimeString()}
            </span>
          </div>
        )}
      </div>

      <div className="text-center mb-14 mt-10">
        {/* <div className="relative w-28 h-28 mx-auto mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-green-600 rounded-full animate-glow-pulse shadow-[0_0_40px_rgba(220,38,38,0.6)]"></div>
          <div className="absolute inset-[6px] bg-black rounded-full flex items-center justify-center">
            <Lightbulb className="w-12 h-12 text-white animate-bounce" />
          </div>
        </div> */}
        {/* <h1  className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-green-400 to-red-500 tracking-tight mb-6 animate-gradient">
          Submit Your Idea
        </h1> */}
        {/* <p className="text-gray-200 text-base md:text-lg mb-6">
          Welcome to Udbhav!<br />
       
        </p> */}
        {draftId && (
          <p className="text-green-400 mb-2">
            {/* Editing draft - your work is being auto-saved every 30 seconds */}
          </p>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-black rounded-xl p-6 text-white shadow-xl">
          <DialogHeader className="text-center">
            <DialogTitle className="text-2xl font-semibold bg-gradient-to-r from-green-400 via-red-400 to-green-400 bg-clip-text text-transparent">
              Idea Submitted Successfully!
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4 text-center">
            <p className="text-gray-300 mb-3">
              Thank you for your submission.<br/>
              Kindly note your Use Case ID for future reference.
            </p>

            <div className="flex items-center justify-between bg-white/20 backdrop-blur-sm px-4 py-3 rounded-lg mt-2">
              <span className="font-mono font-semibold text-white text-lg">
                {useCaseId}
              </span>

              <Button
                variant="outline"
                className=" text-white hover:bg-white hover:text-black transition"
                onClick={() => {
                  navigator.clipboard.writeText(useCaseId);
                  toast.success("Copied to clipboard!");
                }}
              >
                Copy
              </Button>
            </div>
          </div>

          <DialogFooter className="mt-6 flex justify-center">
            <Button
              className="bg-white text-black hover:bg-gray-200 font-semibold px-6"
              onClick={() => {
                setShowModal(false);
                navigate("/", { replace: true });
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="max-w-6xl mx-auto relative z-10">
<p className="text-center">
  Update and Resubmit Use Case Idea
</p>

        {/* SUBMISSION TYPE */}
        <Section >
<RadioGroup
  value={submissionType}
  onValueChange={setSubmissionType}
  className="flex justify-center"
>

            {/* NEW IDEA */}
            {/* <div
              className={`custom-radio flex flex-col items-start p-6 rounded-xl border cursor-pointer transition-all h-full ${
                submissionType === "new"
                  ? "border-green-500 bg-green-900/30 "
                  : "border-gray-700 hover:border-green-400"
              }`}
              onClick={handleNewIdeaClick}
            >
              <div className="flex items-center mb-3">
                <input
                  type="radio"
                  name="submissionType"
                  value="new"
                  checked={submissionType === "new"}
                  onChange={() => {}}
                  className="peer hidden"
                />
                <span className="radio-indicator"></span>

                <Label className="ml-3 text-white text-lg font-semibold cursor-pointer">
                  <span className="text-green-400">New Idea</span>
                </Label>
              </div>

              <p className="text-sm text-gray-400">
                Submit a fresh concept or innovative solution.
              </p>
            </div> */}

<div
  className={`custom-radio flex flex-col items-center p-6 rounded-xl border cursor-pointer transition-all max-w-md w-full ${
    submissionType === "update"
      ? "border-green-500 bg-green-900/30"
      : "border-gray-700 hover:border-green-400"
  }`}
  onClick={() => {
    if (!isLoggedIn) {
      navigate(`/login?redirect=submit-idea`);
      return;
    }
    setSubmissionType("update");
  }}
>

              <div className="flex items-center mb-3">
                <input
                  type="radio"
                  name="submissionType"
                  value="update"
                  checked={submissionType === "update"}
                  onChange={() => {}}
                  className="peer hidden"
                />
                <span className="radio-indicator"></span>

                <Label className="ml-3 text-white text-lg font-semibold cursor-pointer">
                  <span className="text-green-400">Resubmit Udbhav 1.0 Idea</span>
                </Label>
              </div>

   <p className="text-sm text-gray-400 text-center">
  Update and resubmit your previously submitted idea
</p>

            </div>
          </RadioGroup>
        </Section>

        {/* NEW IDEA FORM */}
        {/* {submissionType === "new" && isLoggedIn ? ( */}
    {submissionType === "new"? (
          <div>
{/* OFFLINE / GOOGLE FORM FILLING SECTION */}
{/* <div className="mt-8 mb-10 space-y-6">

  <div className="bg-gradient-to-r from-green-600/40 via-green-500/20 to-red-600/40 p-5 rounded-xl border border-green-600/40 shadow-md">
    <h3 className="text-lg font-semibold text-white mb-2">
      Prefer filling offline?
    </h3>

    <p className="text-gray-300 leading-relaxed">
      You can download the{" "}
      <a
        href="/TEMPLATE-Udbhav 2_Idea_Submission.xlsx"
        className="text-green-400 underline hover:text-green-300"
      >
        Idea Submission Template
      </a>
      , fill it at your convenience, and email the completed file to{" "}
      <a
        href="mailto:udbhav@oges.co"
        className="text-green-400 underline hover:text-green-300"
      >
        udbhav@oges.co
      </a>.
    </p>

    <a href="/TEMPLATE-Udbhav 2_Idea_Submission.xlsx" download className="inline-block mt-4">
      <Button
        variant="outline"
        className="
          border-green-500 
          text-green-400 
          hover:bg-green-500/20 
          hover:text-green-300 
          transition-all 
          duration-200 
          flex items-center gap-2 px-6 py-3 rounded-xl
        "
      >
        <Download className="w-4 h-4" />
        Download Template
      </Button>
    </a>
  </div>

  <div className="flex items-center justify-center">
    <span className="px-4 py-1 text-sm font-semibold text-gray-300 bg-gray-800 rounded-full border border-gray-600">
      OR
    </span>
  </div>

<div className="bg-gradient-to-r from-green-600/40 via-green-500/20 to-red-600/40 p-5 rounded-xl border border-green-600/40 shadow-md">
  <h3 className="text-lg font-semibold text-white mb-2">
    Prefer submitting via Google Form?
  </h3>

  <p className="text-gray-300 leading-relaxed mb-4">
    You can also submit your idea using our Google Form.
    <br />
    <span className="text-gray-400">
      Fill in the required details directly in the form below and submit it to
      complete your submission.
    </span>
  </p>

  <div className="w-full rounded-xl overflow-hidden border border-green-600/40">
    <iframe
      src="https://docs.google.com/forms/d/e/1FAIpQLSekok3wYw-j3eSw0OGBB1amsopP7PzZ4H0NHkQ9WDcFLEPyfA/viewform"
      className="w-full h-[70vh]"
      style={{ border: "none" }}
      title="Udbhav Idea Submission Google Form"
    />
  </div>
</div>



</div> */}


            {/* BASIC IDEA DETAILS */}
            {/* <Section title="Basic Idea Details">
              <div className="grid md:grid-cols-2 gap-6">
                <InputField
                  label="Team Name"
                  name="teamName"
                  placeholder="Enter your team name"
                  required
                />
                <InputField
                  label="ONGC WorkCenter"
                  name="workCenter"
                  placeholder="Enter work center"
                  required
                />
                <InputField
                  label="POC Done"
                  name="pocDone"
                  placeholder="Yes / No"
                  required
                />
              </div>
            </Section> */}

            {/* TEAM MEMBERS */}
            {/* <Section title="Team Members Information">
              <div className="overflow-x-auto rounded-lg border border-gray-700">
                <table className="w-full text-sm">
                  <thead className="bg-green-950/40 border-b border-green-700/50 text-green-200">
                    <tr>
                      <th className="p-2">Role</th>
                      <th className="p-2">First</th>
                      <th className="p-2">Last</th>
                      <th className="p-2">Email</th>
                      <th className="p-2">Emp ID</th>
                      <th className="p-2">Dept</th>
                      <th className="p-2">Designation</th>
                      <th className="p-2">Location</th>
                      <th className="p-2 text-center w-12"></th>
                    </tr>
                  </thead>

                  <tbody>
                    {teamMembers.map((member, index) => (
                      <tr
                        key={index}
                        className="border-t border-gray-700 hover:bg-green-900/20"
                      >
                        <td className="p-2 text-white">{member.role}</td>

                        {[
                          "firstName",
                          "lastName",
                          "email",
                          "empId",
                          "department",
                          "designation",
                          "location",
                        ].map((field) => (
                          <td className="p-2" key={field}>
                            <InputField
                              name={`${member.role}_${field}`}
                              placeholder={
                                field.charAt(0).toUpperCase() + field.slice(1)
                              }
                              required={member.role === "Team Leader" && field === 'firstName'}
                              teamMemberField={true}
                            />
                          </td>
                        ))}

                        <td className="p-2 text-center">
                          {index > 0 && (
                            <button
                              onClick={() => removeMember(index)}
                              className="text-green-400 hover:text-green-600"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  onClick={addMember}
                  className="flex items-center gap-2 border-green-500 text-green-400 hover:bg-green-500/20"
                >
                  <Plus size={16} />
                  Add Team Member
                </Button>
              </div>
            </Section> */}

            {/* BUSINESS PROBLEM */}
            {/* <Section title="Business Problem">
              <TextareaField
                label="Crisp Use Case Title"
                name="useCaseTitle"
                placeholder="One-line summary"
                required
              />
              <TextareaField
                label="Business Problem (Problem Statement : What are you trying to solve ?)"
                name="businessProblem"
                placeholder="Describe the problem"
                required
              />
            </Section> */}

            {/* AS IS */}
            {/* <Section title="As-Is State">
          
              <div className="mb-4 border border-gray-600 bg-gray-800/50 rounded-lg overflow-hidden">
                <label className="block p-3 text-white font-medium">
                  As Is State (How is the system currently running, Current Process Overview?)
                </label>

              
                <button
                  type="button"
                  onClick={() => setShowAsIsExample(!showAsIsExample)}
                  className="w-full flex items-center gap-2 p-3 text-green-300 text-sm font-medium hover:bg-gray-700/50 transition-colors text-left"
                >
                  {showAsIsExample ? (
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  View Example
                </button>

                {showAsIsExample && (
                  <div className="p-4 pt-0 text-sm text-gray-300 border-t border-gray-600">
                    <div className="bg-gray-900/50 p-4 rounded">
                      <p className="font-semibold mb-2 text-green-300">Sample:</p>
                      <p className="italic mb-3">
                        "The current offset well analysis process is largely manual, requiring significant time and effort..."
                      </p>
                      <ol className="list-decimal ml-5 space-y-2 text-gray-300">
                        <li><strong>Manual Data Collection:</strong> Engineers retrieve historical drilling, geological, and production data from structured databases.</li>
                        <li><strong>Multidisciplinary Collaboration:</strong> Drilling engineers, geologists, and reservoir engineers manually analyze offset well data.</li>
                        <li><strong>Comparative Analysis:</strong> Teams compare prospect well data with selected offset wells based on experience.</li>
                        <li><strong>Offset Well Selection:</strong> Engineers manually select the most relevant offset wells as templates for planning.</li>
                        <li><strong>Iterative Design Process:</strong> The selected offset well design is modified and refined for the new well.</li>
                        <li><strong>Time-Intensive & Redundant Work:</strong> The process can take weeks, requiring repetitive tasks and rework when new data is introduced.</li>
                        <li><strong>Risk Identification:</strong> Engineers manually extract risks from large volumes of reports, which can be inconsistent and inefficient.</li>
                        <li><strong>Inconsistency:</strong> Offset well selection depends on the experience and judgment of individual team members, leading to variations in analysis."</li>
                      </ol>
                    </div>
                  </div>
                )}

             
                <TextareaField
                  name="asIsOverview"
                  placeholder="Describe current process"
                  required
                />
              </div>

           
              <div className="mb-4 border border-gray-600 bg-gray-800/50 rounded-lg overflow-hidden">
                <label className="block p-3 text-white font-medium">
                  Where in the as-is process do you want to bring the change?
                </label>

                <button
                  type="button"
                  onClick={() => setShowChangeExample(!showChangeExample)}
                  className="w-full flex items-center gap-2 p-3 text-green-300 text-sm font-medium hover:bg-gray-700/50 transition-colors text-left"
                >
                  {showChangeExample ? (
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  View Example
                </button>

                {showChangeExample && (
                  <div className="p-4 pt-0 text-sm text-gray-300 border-t border-gray-600">
                    <div className="bg-gray-900/50 p-4 rounded">
                      <p className="font-semibold mb-2 text-green-300">Sample:</p>
                      <p className="italic mb-3">
                        "The changes are aimed at automating and optimizing key steps to reduce time, effort..."
                      </p>
                      <ol className="list-decimal ml-5 space-y-2 text-gray-300">
                        <li><strong>Automating Data Retrieval:</strong> Eliminating manual data gathering by integrating a system that queries historical data automatically.</li>
                        <li><strong>Replacing Manual Comparison:</strong> Analysts: Using AI/ML algorithms to compare well trajectories, geological data, and drilling performance.</li>
                        <li><strong>Improving Offset Well Selection:</strong> Implementing automated ranking of offset wells based on drilling performance metrics (e.g., NPT, cost/ft).</li>
                        <li><strong>Automating Risk Identification:</strong> Using a system-generated risk registry instead of manual risk extraction from reports.</li>
                        <li><strong>Standardizing Decision-Making:</strong> Reducing dependency on individual experience by enabling data-driven well design selection."</li>
                      </ol>
                    </div>
                  </div>
                )}

               
                <TextareaField
                  name="asIsChangePoint"
                  placeholder="Where do you want improvement?"
                  required
                />
              </div>
            </Section> */}

     
            {/* <Section title="Data and Use of AI">
              <TextareaField
                label="What types of data is needed for your solution?"
                name="dataTypes"
                placeholder="Required data types"
              />
              <TextareaField
                label="What are the expected sources of this data?"
                name="dataSources"
                placeholder="Sources of data"
              />
              <TextareaField
                label="Is this data currently accessible to you or your team?"
                name="dataAccessibility"
                placeholder="Is data accessible?"
              />
              
           
              <div className="mb-4 border border-gray-600 bg-gray-800/50 rounded-lg overflow-hidden">
          
                <label className="block p-3 text-white font-medium">
                  Which AI technology do you propose to implement
                </label>

                <button
                  type="button"
                  onClick={() => setShowAITechExample(!showAITechExample)}
                  className="w-full flex items-center gap-2 p-3 text-green-300 text-sm font-medium hover:bg-gray-700/50 transition-colors text-left"
                >
                  {showAITechExample ? (
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  View Example
                </button>

          
                {showAITechExample && (
                  <div className="p-4 pt-0 text-sm text-gray-300 border-t border-gray-600">
                    <div className="bg-gray-900/50 p-4 rounded">
                      <p className="font-semibold mb-2 text-green-300">Sample:</p>
                      <p className="italic text-gray-300">
                        "Neural Network, NLP, Machine Learning"
                      </p>
                    </div>
                  </div>
                )}

       
                <TextareaField
                  name="aiTechnology"
                  placeholder="AI to be used"
                />
              </div>
            </Section> */}

            {/* BENEFITS */}
            {/* <Section title="Business Benefits">
              <TextareaField
                label="What is the benefit of implementing your proposed solution ?"
                name="benefits"
                placeholder="Expected improvements"
              />

            
              <div className="overflow-x-auto mt-6 rounded-lg border border-gray-700">
                <table className="w-full text-sm">
                  <thead className="bg-green-950/40 border-b border-green-700/50 text-green-200">
                    <tr>
                      <th className="p-2"></th>
                      <th className="p-2">3 Months after</th>
                      <th className="p-2">6 Months after</th>
                      <th className="p-2">1 Year after</th>
                      <th className="p-2">3 Years after</th>
                      <th className="p-2">5 Years after</th>
                      <th className="p-2">Logic for estimating approx revenue or cost saving numbers</th>
                    </tr>
                  </thead>

                  <tbody>
                    {[
                      "Increase in Revenue in INR (Crores)",
                      "Cost Saving in INR (Crores)",
                      "Strategic Impact (Alignment with ONGC's Corporate KPIs)",
                    ].map((label, index) => (
                      <tr
                        key={index}
                        className="border-t border-gray-700 hover:bg-green-900/20 text-white"
                      >
                        <td className="p-2 font-medium">{label}</td>

                       
                        {["3m", "6m", "1y", "3y", "5y"].map((period) => (
                          <td className="p-2" key={period}>
                            <InputField
                              name={`${label.replace(/\s+/g, "_")}_${period}`}
                              placeholder="Value"
                            />
                          </td>
                        ))}

                   
                        <td className="p-2">
                          <InputField
                            name={`${label.replace(/\s+/g, "_")}_logic`}
                            placeholder="Explanation"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section> */}
       
            {/* <Section title="References">
              {references.map((ref, index) => (
                <InputField
                  key={index}
                  label={`Research Paper / Web Link ${index + 1}`}
                  name={`reference${index + 1}`}
                  placeholder="Paste URL or title"
                />
              ))}

       
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={addReference}
                  className="flex items-center gap-2 border border-green-500 text-green-400 px-3 py-1 rounded-lg hover:bg-green-500/20"
                >
                  + Add Reference
                </button>
              </div>
            </Section> */}

            {/* <Section title="Team Capability">
    
              <div className="mb-6 border border-[#3b4a52] bg-[#0e1a1d] rounded-lg overflow-hidden shadow-md">
              
                <label className="block p-3 text-white font-medium">
                  Why are you the best team to solve this?
                </label>

            
                <button
                  type="button"
                  onClick={() => setShowBestTeamExample(!showBestTeamExample)}
                  className="w-full flex items-center gap-2 p-3 text-green-300 text-sm font-medium hover:bg-gray-700/50 transition-colors text-left"
                >
                  {showBestTeamExample ? (
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  View Example
                </button>

        
                {showBestTeamExample && (
                  <div className="p-4 pt-0 text-sm text-gray-300 border-t border-[#2f3c42] bg-[#0c1416]">
                    <p className="font-semibold mb-2 text-green-300">Sample:</p>
                    <p className="italic text-gray-400">
                      "We have a multi-disciplinary team with all the required technical, domain, and operational skills to deliver this project efficiently."
                    </p>
                  </div>
                )}

      
                <TextareaField
                  name="whyBestTeam"
                  placeholder="Highlight your team's strengths and capabilities"
                />
              </div>

    
              <div className="mb-6 border border-[#3b4a52] bg-[#0e1a1d] rounded-lg overflow-hidden shadow-md">
           
                <label className="block p-3 text-white font-medium">
                  What additional support do you need?
                </label>

         
                <button
                  type="button"
                  onClick={() => setShowSupportExample(!showSupportExample)}
                  className="w-full flex items-center gap-2 p-3 text-green-300 text-sm font-medium hover:bg-gray-700/50 transition-colors text-left"
                >
                  {showSupportExample ? (
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  View Example
                </button>

                {showSupportExample && (
                  <div className="p-4 pt-0 text-sm text-gray-300 border-t border-[#2f3c42] bg-[#0c1416]">
                    <p className="font-semibold mb-2 text-green-300">Sample:</p>
                    <p className="italic text-gray-400">
                      "We will require support from an AI/ML expert to validate the model architecture and optimize performance metrics."
                    </p>
                  </div>
                )}

             
                <TextareaField
                  name="supportNeeded"
                  placeholder="Describe the support, resources, or expertise needed"
                  required
                />
              </div>
            </Section> */}
{/* 
            <div className="flex flex-wrap justify-end pt-6 gap-4">

        
            {allDrafts.length > 0 && (
              // <Section title="">
                <div className="flex flex-wrap gap-2">
                  {isLoadingDrafts ? (
                    <div className="flex items-center gap-2 px-3 py-2 text-gray-400">
                      <Clock className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Loading drafts...</span>
                    </div>
                  ) : (
                    allDrafts.map((draft) => (
                      <Button
                        key={draft.id}
                        onClick={() => loadSpecificDraft(draft.id)}
                        variant="outline"
                        size="sm"
                        className={`
                          border-green-500 text-green-400 hover:bg-green-500/20 hover:text-green-300 transition-all duration-200 flex items-center gap-2 px-6 py-3 rounded-xl
                        `}
                        title={`${draft.type === 'autosave' ? 'Autosave' : 'Draft'} - ${draft.timestamp}`}
                      >
                        <BookOpen className="w-3 h-3" />
                        {draft.displayLabel}
                        <span className="text-xs opacity-70 ml-1">({draft.shortTime})</span>
                      </Button>
                    ))
                  )}
                </div>
              // </Section>
            )}

           
              <Button
                onClick={saveAsDraft}
                variant="outline"
                className="border-green-500 text-green-400 hover:bg-green-500/20 hover:text-green-300 transition-all duration-200 flex items-center gap-2 px-6 py-3 rounded-xl"
                disabled={isAutoSaving}
                size="sm"
              >
                <Save className="mr-2 w-4 h-4" />
                {isAutoSaving ? "Saving..." : "Save as Draft"}
              </Button>

              <Button
                onClick={handleSubmit}
                className="bg-gradient-to-r from-green-700 via-green-600 to-green-400 text-white flex items-center px-8 py-3 rounded-xl"
                disabled={isAutoSaving}
                size="sm"
              >
                Submit Idea
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div> */}
          </div>
        ) : (
          // UPDATE IDEA FLOW
          <>
            {submissionType === "update" && (
              <>
                {isLoggedIn || emailFromQuery ? (
                  <OldSubmit />
                ) : (
                  <Section title="Login to Udbhav 1.0">
                    <p className="text-sm text-gray-300 mb-4">
                      Use the same email ID that you used while submitting ideas in Udbhav 1.0
                    </p>
             
                    <InputField
                      label="Email"
                      name="email"
                      placeholder="Enter your email"
                      required
                    />
             
                    <div className="flex justify-end pt-6">
                      <Button
                        onClick={handleOldLogin}
                        className="bg-gradient-to-r from-blue-700 via-blue-600 to-blue-400 text-white flex items-center px-8 py-3 rounded-xl"
                      >
                        Login
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </Section>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubmitIdea;